import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X } from "lucide-react";
import BrandWordmarkText from "./BrandWordmarkText";

// Add-to-home-screen affordance. The browser fires `beforeinstallprompt` only when the app is
// genuinely installable (served over https, manifest valid, not already installed) — so this
// banner never renders in dev/e2e/headless and adds no a11y surface there. Dismissal is
// remembered for the session so we don't nag.
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem("pwa-install-dismissed") === "1"; } catch { return false; }
  });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const returnFocusRef = useRef(null);
  // Hide only on the marketing landing page ("/"); it may show on every other route.
  const { pathname } = useLocation();
  const onLanding = pathname === "/";

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // When the banner becomes available, remember where focus was (to restore on teardown) and
  // announce its arrival politely so screen-reader users know an actionable surface appeared.
  useEffect(() => {
    if (!deferred || hidden) return undefined;
    setMsg("");
    if (document.activeElement instanceof HTMLElement) returnFocusRef.current = document.activeElement;
    const id = window.setTimeout(() => setMsg("Vidyapeeth360 can be added to your home screen."), 120);
    return () => window.clearTimeout(id);
  }, [deferred, hidden]);

  if (!deferred || hidden || onLanding) return null;

  const restoreFocus = () => {
    window.requestAnimationFrame(() => {
      const el = returnFocusRef.current;
      if (el instanceof HTMLElement && el.isConnected) el.focus({ preventScroll: true });
    });
  };

  const dismiss = () => {
    setHidden(true);
    setMsg("");
    try { sessionStorage.setItem("pwa-install-dismissed", "1"); } catch { /* ignore */ }
    restoreFocus();
  };

  const install = async () => {
    if (busy) return;
    setBusy(true);
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* ignore */ } finally {
      setDeferred(null);
      setBusy(false);
      setMsg("");
      restoreFocus();
    }
  };

  return (
    <div
      role="region"
      aria-labelledby="pwa-install-title"
      // Lifted CLEAR of the Aira FAB, which is fixed bottom-4 right-4 at z-50 — above this
      // banner's z-30 and in the same corner, so it covered this card's dismiss button: the
      // banner could not be closed, and while it stayed up it intercepted the primary action
      // beneath it (reproduced 2026-08-31: the /register wizard's "Next" was unclickable at a
      // ~780px viewport). 1rem base + the FAB's 3.5rem + a 0.75rem gap.
      style={{ bottom: "calc(1rem + 3.5rem + 0.75rem + env(safe-area-inset-bottom))" }}
      // Bottom-RIGHT corner on ≥sm (was bottom-center, which overlapped primary content like
      // empty-states); full-width bottom sheet only on phones where there's no room for a corner.
      className="fixed right-4 left-4 sm:left-auto z-30 sm:w-[22rem] max-w-[calc(100%-2rem)]
                 bg-surface rounded-2xl border border-line shadow-pop p-3.5 flex items-center gap-3
                 animate-fade-in motion-reduce:animate-none"
    >
      <p aria-live="polite" className="sr-only">{msg}</p>
      <div className="w-10 h-10 rounded-xl bg-accent/10 grid place-items-center shrink-0">
        <Download className="w-5 h-5 text-accent" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p id="pwa-install-title" className="text-sm font-semibold text-ink leading-tight">Install <BrandWordmarkText className="text-sm" /></p>
        <p className="text-xs text-ink-2 leading-tight mt-0.5">Add to your home screen for one-tap access — works offline too.</p>
      </div>
      <button
        type="button"
        onClick={install}
        disabled={busy}
        aria-busy={busy}
        data-testid="pwa-install-btn"
        className="shrink-0 inline-flex items-center justify-center min-h-11 px-4 rounded-xl text-sm font-semibold text-white bg-primary hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 transition-colors
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
      >
        Install
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="shrink-0 inline-flex items-center justify-center h-11 w-11 -mr-1.5 rounded-lg text-ink-2 hover:bg-muted active:bg-muted transition-colors
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
